import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useState } from "react";
import { QueryClient, useMutation } from "react-query";

import Search from "components/Search/Search";
import { handleError } from "helpers/utils";

import AddJob from "./partials/AddJobs/AddJobs";
import EditJob from "./partials/EditJobs/EditJobs";
import DeleteJob from "./partials/DeleteJobs/DeleteJobs";
import { IJob, IJobInput } from "models/job";
import { JobsCells, addJob, deleteJob, editJob, getJobs } from "framework/jobs";
import { useTranslation } from "react-i18next";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";
interface Props {
  feature: Feature;
}

const Jobs: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterJobs, setFilterJobs] = useState<IJob[]>([] as IJob[]);
  const { t, i18n } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<IJob>({} as IJob);
  const [inputObj, setInputObj] = useState<IJobInput>({} as IJobInput);
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addDepartmentsMutation = useMutation({
    mutationFn: (createInput: IJobInput) => {
      return addJob(createInput);
    },
  });

  const editDepartmentsMutation = useMutation({
    mutationFn: (data: { createInput: IJobInput; id: string }) => {
      return editJob(data.createInput, data.id);
    },
  });

  const deleteDepartmentsMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteJob(id);
    },
  });

  const {
    loading,
    data: Jobs,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getJobs(),
    fetchPermission: () => getPermission(13),
    section: "Jobs",
    onSuccess: (data) => setFilterJobs(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Country: IJob) => {
    setSelectedJob(Country);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedJob({} as IJob);
  };

  const handleOpenDelete = (Country: IJob) => {
    setSelectedJob(Country);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedJob({} as IJob);
  };

  const getBodyCells = (row: IJob) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "title")
        return {
          id: "title",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.title}</Typography>
            </TableCell>
          ),
        };

      if (key === "titleAr")
        return {
          id: "titleAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.titleAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "description")
        return {
          id: "description",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.description}</Typography>
            </TableCell>
          ),
        };

      if (key === "descriptionAr")
        return {
          id: "descriptionAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.descriptionAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (
      !inputObj.title ||
      !inputObj.description ||
      !inputObj.titleAr ||
      !inputObj.descriptionAr
    )
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addDepartmentsMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Job added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as IJobInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IJobInput = {
        title: inputObj.title || selectedJob.title,
        description: inputObj.description || selectedJob.description,
        titleAr: inputObj.titleAr || selectedJob.titleAr,
        descriptionAr: inputObj.descriptionAr || selectedJob.descriptionAr,
      };
      await editDepartmentsMutation.mutateAsync({
        createInput,
        id: selectedJob.id,
      });
      setAlert({
        open: true,
        message: "Job updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as IJobInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteDepartmentsMutation.mutateAsync(selectedJob.id);
      setAlert({
        open: true,
        message: "Job deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();

      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterJobs(Jobs!);
    let filtered = Jobs!.filter((job) =>
      job.title.toLowerCase().includes(value.toLowerCase())
      || job.titleAr?.toLowerCase()?.includes(value.toLowerCase())
      || job.description?.toLowerCase()?.includes(value.toLowerCase())
      || job.descriptionAr?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterJobs(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title="Add Jobs">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterJobs.length > 0 && (
          <List
            headCells={JobsCells}
            data={FilterJobs}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterJobs || FilterJobs.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_job_header")}
              message={t("T_no_job_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddJob
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditJob
          open={openEdit}
          loading={loading2}
          job={selectedJob}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteJob
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Jobs;
